<template>
	<ClientOnly>
		<Teleport to="body" :disabled="disableTeleport">
			<div class="base-modal-container" @mousedown.self="$emit('close', false)">
				<div class="base-modal" :style="backgroundColor ? { backgroundColor } : {}">
					<div class="base-modal__header" v-if="hasHeader">
						<h2 v-if="$slots.title" class="base-modal__header__title">
							<slot name="title" />
						</h2>

						<div v-if="closable" class="base-modal__header__close">
							<slot name="header-info"></slot>
							<FontAwesomeIcon :icon="faTimes" @click="$emit('close', false)" />
						</div>
					</div>

					<div class="base-modal__content">
						<slot />
					</div>

					<div v-if="hasFooter" class="base-modal__footer">
						<slot name="footer" />
					</div>
				</div>
			</div>
		</Teleport>
	</ClientOnly>
</template>

<script setup lang="ts">
import { watch } from 'vue';

import { enableScroll } from '@/helpers/scroll-helper';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';

const { activeRoute } = storeToRefs(useRoutingStore());

withDefaults(
	defineProps<{
		closable?: boolean;
		hasHeader?: boolean;
		hasFooter?: boolean;
		disableTeleport?: boolean;
		backgroundColor?: string;
	}>(),
	{
		closable: true,
		hasHeader: true,
	}
);

const emit = defineEmits<{
	(e: 'close', display: boolean): void;
}>();

watch(activeRoute, () => {
	enableScroll();
	emit('close', false);
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.base-modal-container {
	position: fixed;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	width: 100%;
	height: 100%;
	background: rgba(6, 13, 23, 0.88);
	z-index: 101; // Navbar is 100 so this needs to be 101
	display: flex;
	justify-content: center;
	align-items: center;

	@media (max-width: 767px) {
		align-items: flex-end;
	}
}

.is-modal-inception {
	width: 100%;
	height: calc(100% - 56px);
	margin-top: 56px;
	overflow-y: scroll;
}

.base-modal {
	height: fit-content;
	width: fit-content;
	min-width: 40vw;
	max-width: 60vw;
	max-height: 80dvh;
	background: var(--background-bg-low-active, #1a2029);
	display: flex;
	flex-direction: column;
	align-items: center;
	border-radius: 8px;

	@media (max-width: 767px) {
		min-width: 100%;
		min-height: 30dvh;
		height: fit-content;
		max-height: 80dvh;
		border-radius: 16px 16px 0 0;
	}

	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
		padding: 24px;

		@media (max-width: 767px) {
			padding: 16px;
			padding-bottom: 8px;
		}

		&__title {
			color: #909398;
			font-size: 14px;
			font-weight: 400;
			line-height: 16px;
			letter-spacing: 0.21px;
			margin: 0;
		}

		&__close {
			font-size: 18px;
			display: flex;
			justify-content: flex-end;
			align-items: center;

			&:hover {
				cursor: pointer;
			}
		}
	}

	&__content {
		width: 100%;
		padding: 24px;
		padding-top: 0;
		flex-grow: 1;
		overflow-y: auto;

		@media (max-width: 767px) {
			padding: 16px;
			padding-top: 8px;
		}
	}

	&__footer {
		width: 100%;
		padding: 24px;
		padding-top: 0;
	}
}
</style>
